/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import type { THue } from 'volta/utils/style-utils';

import { isArray } from '@ember/array';
import { assert } from '@ember/debug';
import Component from '@glimmer/component';

import type { BvBadgeArgs } from '../bv-badge/index';
import type { IBtnColor } from '../bv-btn/index';

import type { ContentValue } from '@glint/template';
import type Transition from '@ember/routing/transition';

export interface IBvMenuItem {
  hasArrow?: boolean;
  isLoading?: boolean;
  active?: boolean;
  href?: string;
  model?: object;
  icon?: string;
  iconColor?: string;
  content?: ContentValue;
  suffix?: ContentValue;
  color?: THue | IBtnColor;
  selected?: boolean;
  onClick?: (event: Event) => void | Promise<void> | Transition;
  disabled?: boolean;
  tooltip?: string;
  title?: string;
  badge?: BvBadgeArgs;
  prefix?: ContentValue;
  shortcut?: ContentValue;
}

export interface IBvMenuSection {
  title?: string;
  items: (IBvMenuItem | undefined)[];
}

interface BvMenuSignature {
  Args: {
    // Collection of actions for list
    items?: (IBvMenuItem | undefined)[];
    // Collection of sectioned action items
    sections?: IBvMenuSection[];
    // Callback when any item is clicked or keypressed
    onAnyItemAction?: () => void;
    hasMultipleSections?: boolean;
  };
  Blocks: {
    default: [unknown];
  };
}

export default class BvMenuComponent extends Component<BvMenuSignature> {
  get hasMultipleSections() {
    return this.finalSections.length > 1;
  }

  /**
   * It takes the sections array and adds the items array to it.
   * @returns The finalSections array.
   */
  get finalSections() {
    const { items = [], sections = [] } = this.args;
    const validItems = items.filter(Boolean);
    const validSections = sections.reduce<IBvMenuSection[]>((acc, s) => {
      if (s) {
        const items = s.items.filter(Boolean);
        if (items.length > 0) {
          acc.push({ ...s, items });
        }
      }
      return acc;
    }, []);
    const finalSections = [];

    if (validItems.length > 0) {
      finalSections.push({ items: validItems });
    }

    assert(
      `bevolta::bv-menu - sections must be an array, you passed ${sections}`,
      isArray(sections)
    );

    if (validSections.length > 0) {
      finalSections.push(...validSections);
    }

    return finalSections;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvMenu: typeof BvMenuComponent;
    'bv-menu': typeof BvMenuComponent;
  }
}
